import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Link,
} from '@fluentui/react-components';
import React, { ReactNode } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { Flex } from '@tdp/common/fluentV9MigrationComponents';
import { selfClickOnEnterOrSpace } from '@tdp/common/shared/AccessibilityUtils';
import { ThemeControl } from '@tdp/common/theme';

import { useStyles } from './MenuFooter.Styles';

interface IMenuFooter {
  horizontal?: boolean;
}

export const Links = {
  Privacy: 'https://go.microsoft.com/fwlink/?LinkId=521839',
  ItalianAccessibilityDeclaration:
    'https://www.microsoft.com/it-it/accessibility/declarations',
  TermsOfUse: 'https://aka.ms/st1pbotttermsofservice',
};

export const getFormattedStrings = (intl: IntlShape) => ({
  changeTheme: intl.formatMessage({
    defaultMessage: 'Change theme',
    description: 'Change theme display text',
  }),
  themesText: intl.formatMessage({
    defaultMessage: 'Themes',
    description: 'Themes',
  }),
  italianAccessibilityDeclaration: intl.formatMessage({
    defaultMessage: 'Accessibility',
    description: 'This is the lablel for Italian accessibility declaration.',
  }),
  privacy: intl.formatMessage({
    defaultMessage: 'Privacy and cookies',
    description: 'Privacy link display text',
  }),
  termsOfUse: intl.formatMessage({
    defaultMessage: 'Terms of use',
    description: 'Terms of use display text',
  }),
  closeLabel: intl.formatMessage({
    defaultMessage: 'close',
    description: 'close',
  }),
});

const FooterChangeThemeLink: React.FC = () => {
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const classes = useStyles();
  const intl = useIntl();
  const fs = getFormattedStrings(intl);
  const onChangeThemeClick = () => {
    setOpenDialog(true);
  };

  return (
    <Dialog
      open={openDialog}
      onOpenChange={(event, { open }) => {
        event.stopPropagation();
        setOpenDialog(open);
      }}
    >
      <DialogTrigger disableButtonEnhancement>
        <Link
          role="button"
          className={classes.appLinkStyle}
          onClick={onChangeThemeClick}
          data-testid="change-theme-link"
        >
          {fs.changeTheme}
        </Link>
      </DialogTrigger>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{fs.themesText}</DialogTitle>
          <DialogContent>
            <ThemeControl />
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">{fs.closeLabel}</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const MenuFooter: React.FC<IMenuFooter> = ({ horizontal }) => {
  const classes = useStyles();
  const intl = useIntl();
  const fs = getFormattedStrings(intl);

  const FooterPageLink: React.FC<{ href: string; children: ReactNode }> = ({
    children,
    href,
  }) => (
    <Link
      role={'button'}
      className={classes.pageLinkStyle}
      href={href}
      target="_blank"
      onKeyDown={selfClickOnEnterOrSpace}
    >
      {children}
    </Link>
  );

  const isItalianLocale = intl.locale === 'it-it' || false;

  return (
    <Flex
      id="MenuFooter"
      column={!horizontal}
      gap={horizontal ? 'gap.smaller' : undefined}
      className={classes.container}
      role="group"
      data-testid="nav-menu-footer"
    >
      <FooterChangeThemeLink />
      <FooterPageLink href={Links.Privacy}>{fs.privacy}</FooterPageLink>
      {isItalianLocale && (
        <FooterPageLink href={Links.ItalianAccessibilityDeclaration}>
          {fs.italianAccessibilityDeclaration}
        </FooterPageLink>
      )}
      <FooterPageLink href={Links.TermsOfUse}>{fs.termsOfUse}</FooterPageLink>
    </Flex>
  );
};
